import { canUseDOM } from "@omni/utils/dom.utils";
import { attempt } from "@omni/utils/function.utils";
import { Reducer } from "redux";

import { Config } from "@/utils/config.utils";
import { parseElementForCommentedContent } from "@/utils/ssr";
import { flattenObj } from "@/utils/translations";

import { VALUE_STAGES } from "./create-value-module";
import { rebuildUrls } from "./modules/value/flyoutCategories/helpers/flyoutCategories.helpers";
import { $ValueModuleName, IState, TStore, TValueAction } from "./store.types";
import { formatSingleContants } from "./utils";

type TComponentDataInfo = {
    key?: TValueStateKey;
    state?: Object;
};

const DataMappers: Partial<{
    [K in TValueStateKey]: (data: any) => IState["value"][K];
}> = {
    flyoutCategories: (data) => rebuildUrls(data, `/${Config.getLanguage()}/`),
    translations: (data) => ({ data: flattenObj(data.data), debug: data.debug })
};

type TValueStateKey = keyof IState["value"];

const parseComponent = (component: HTMLElement): TComponentDataInfo => {
    return attempt<TComponentDataInfo>(() => {
        const key = component.dataset.ssrInitialdata as
            | TValueStateKey
            | undefined;
        const json = parseElementForCommentedContent(component);
        return {
            key,
            state: json
        };
    }, {});
};

const resolveInitialState = (store: TStore) => {
    if (canUseDOM()) {
        const elementNodeListOf: NodeList = document.querySelectorAll(
            "script[data-ssr-initialdata]"
        );
        const nodes: Node[] = Array.from(elementNodeListOf);
        nodes.forEach((node) => {
            const { key, state } = parseComponent(node as HTMLElement);
            if (key && state) {
                const type = formatSingleContants(
                    $ValueModuleName,
                    key,
                    VALUE_STAGES.Init
                );

                const mappedState = DataMappers[key]
                    ? DataMappers[key]?.(state)
                    : state;

                store.dispatch({
                    type,
                    payload: mappedState
                });
            }
        });
    }
};

const initializingReducer =
    <S, TStateType, TActionType extends TValueAction>(
        key: keyof TStateType
    ): Reducer<S | undefined, TActionType> =>
    (state: any = {}, action: TActionType) => {
        const { type, payload } = action;
        const matches = /(.*)\/(.*)\/(init)/.exec(type);

        if (!matches) return state;

        const [, prefix, namespace, valueStage] = matches;
        if (prefix !== $ValueModuleName || key !== namespace) {
            return state;
        }

        if (valueStage.toUpperCase() === VALUE_STAGES.Init) {
            return payload;
        }

        return state;
    };

export { initializingReducer, resolveInitialState };
